import React from 'react';
import { useVariant } from '../../contexts/VariantContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useDomainManager } from '../../config';

const VariantTestimonials = () => {
  const { variant } = useVariant();
  const { brand, domainConfig } = useTheme();
  const domainManager = useDomainManager(brand?.id);

  // Check if testimonials should be shown based on domain configuration
  if (!domainConfig?.showTestimonials) {
    return null;
  }

  // Get domain-specific testimonials using the unified configuration
  const getDomainTestimonials = () => {
    const testimonials = domainManager.getTestimonials();
    if (testimonials && testimonials.length > 0) {
      return testimonials;
    }
    
    // If no testimonials are configured, return null to hide the section
    return null;
  };

  const testimonials = getDomainTestimonials();
  
  // If no testimonials are configured, don't render the component
  if (!testimonials) {
    return null;
  }

  // Variant-specific styling
  const getVariantClasses = () => {
    switch (variant) {
      case 'minimal':
        return {
          container: 'bg-white',
          card: 'bg-white border border-gray-200 shadow-sm',
          text: 'text-gray-900',
          textSecondary: 'text-gray-600',
          accent: 'text-gray-400'
        };
      case 'modern':
        return {
          container: 'bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900',
          card: 'bg-white/5 backdrop-blur-md border border-white/10',
          text: 'text-white',
          textSecondary: 'text-purple-100',
          accent: 'text-purple-200'
        };
      case 'corporate':
        return {
          container: 'bg-white',
          card: 'bg-white border border-gray-200 shadow-sm',
          text: 'text-gray-900',
          textSecondary: 'text-gray-600',
          accent: 'text-blue-600'
        };
      case 'creative':
        return {
          container: 'bg-gradient-to-br from-orange-100 via-pink-100 to-purple-100',
          card: 'bg-white/80 backdrop-blur-sm border border-white/50 shadow-lg',
          text: 'text-gray-900',
          textSecondary: 'text-gray-700',
          accent: 'text-orange-500'
        };
      case 'elegant':
        return {
          container: 'bg-gradient-to-br from-blue-50 to-white',
          card: 'bg-white/80 backdrop-blur-sm border border-blue-200/50 shadow-sm',
          text: 'text-blue-900',
          textSecondary: 'text-blue-600',
          accent: 'text-blue-500'
        };
      case 'dark':
        return {
          container: 'bg-gray-900',
          card: 'bg-white/5 backdrop-blur-md border border-white/10',
          text: 'text-white',
          textSecondary: 'text-gray-300',
          accent: 'text-purple-200'
        };
      default:
        return {
          container: 'bg-white',
          card: 'bg-white border border-gray-200 shadow-sm',
          text: 'text-gray-900',
          textSecondary: 'text-gray-600',
          accent: 'text-blue-600'
        };
    }
  };

  const variantClasses = getVariantClasses();

  return (
    <div className={`py-24 ${variantClasses.container}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <span className={`inline-block py-1 px-3 text-xs font-semibold rounded-full ${
            variant === 'modern' ? 'bg-white/10 text-white border border-white/20' :
            variant === 'dark' ? 'bg-white/10 text-white border border-white/20' :
            variant === 'creative' ? 'bg-white/20 text-gray-800 border border-white/30' :
            variant === 'elegant' ? 'bg-slate-100 text-slate-600 border border-slate-200' :
            'bg-blue-50 text-blue-600 border border-blue-200'
          }`}>
            Testimonials
          </span>
          <h2 className={`mt-3 text-3xl font-extrabold sm:text-4xl ${variantClasses.text}`}>
            Trusted by Industry Leaders
          </h2>
          <p className={`mt-4 text-xl max-w-2xl mx-auto ${variantClasses.textSecondary}`}>
            See what our customers have to say about our solutions.
          </p>
        </div>
        
        <div className="mt-16 grid gap-8 lg:grid-cols-2">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className={`rounded-2xl shadow-lg p-8 ${variantClasses.card} ${
                variant === 'creative' ? 'transform hover:rotate-1 transition-transform duration-300' :
                variant === 'elegant' ? 'hover:shadow-xl transition-shadow duration-300' :
                'hover:shadow-xl transition-shadow duration-300'
              }`}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.author}
                    className="w-12 h-12 rounded-full"
                  />
                  <div>
                    <p className={`text-lg font-semibold ${variantClasses.text}`}>
                      {testimonial.author}
                    </p>
                    <p className={`text-sm ${variantClasses.textSecondary}`}>
                      {testimonial.role}, {testimonial.company}
                    </p>
                  </div>
                </div>
                <div className={variantClasses.accent}>
                  <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391C14.017 10.257 16.274 8 19.625 8 20.937 8 22 9.063 22 10.375v1.25C22 12.937 20.937 14 19.625 14h-1.25v7h-4.358z" />
                    <path d="M3.5 21v-7.391C3.5 10.257 5.757 8 9.108 8 10.42 8 11.483 9.063 11.483 10.375v1.25C11.483 12.937 10.42 14 9.108 14h-1.25v7H3.5z" />
                  </svg>
                </div>
              </div>
              <p className={`leading-relaxed ${variantClasses.textSecondary}`}>{testimonial.content}</p>
              <div className="mt-6 flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg key={star} className={`h-5 w-5 ${
                    variant === 'modern' ? 'text-purple-400' :
                    variant === 'dark' ? 'text-blue-400' :
                    variant === 'creative' ? 'text-orange-400' :
                    variant === 'elegant' ? 'text-amber-400' :
                    'text-yellow-400'
                  }`} fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VariantTestimonials;
